import type { RatingValue } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import type { IndicatorBand } from '@risksmart-app/domain/src/types/indicator-banding-config';

import { ConformanceIndicatorRating } from './types';

export type IndicatorReferenceValues = {
  upperTolerance?: null | number;
  lowerTolerance?: null | number;
  upperAppetite?: null | number;
  lowerAppetite?: null | number;
};

export type IndicatorReferenceLine = {
  value: number;
  color: string;
  label: string;
};

type IndicatorRatingResolvers = {
  getColorClass: (value: RatingValue) => null | string;
  getLabel: (value: RatingValue) => string;
};

/**
 * Shared construction of an indicator's tolerance/appetite reference lines —
 * the single source of truth for the line colours, labels, value selection and
 * ordering used by both the indicator results-tab chart (`ResultsChart`, via
 * Cloudscape threshold series) and the dashboard line-chart widget
 * (`IndicatorResultsOverTime`, via Highcharts plotLines). Tolerance bounds use
 * the "Outside" conformance rating, appetite bounds the "Outside appetite"
 * rating. Nil bounds are skipped. Pass the resolvers from
 * `useRating('indicator_conformance_status')`; each consumer maps the returned
 * lines into its own chart library's shape.
 */
export const buildIndicatorReferenceLines = (
  {
    upperTolerance,
    lowerTolerance,
    upperAppetite,
    lowerAppetite,
  }: IndicatorReferenceValues,
  { getColorClass, getLabel }: IndicatorRatingResolvers
): IndicatorReferenceLine[] =>
  [
    { value: upperTolerance, rating: ConformanceIndicatorRating.Outside },
    {
      value: upperAppetite,
      rating: ConformanceIndicatorRating.OutsideAppetite,
    },
    {
      value: lowerAppetite,
      rating: ConformanceIndicatorRating.OutsideAppetite,
    },
    { value: lowerTolerance, rating: ConformanceIndicatorRating.Outside },
  ].flatMap(({ value, rating }) =>
    value == null
      ? []
      : [
          {
            value,
            color: getColorStyles(getColorClass(rating) ?? '').backgroundColor,
            label: getLabel(rating),
          },
        ]
  );

/**
 * Config-driven sibling of {@link buildIndicatorReferenceLines} — CUS-188,
 * `configurable_thresholds` flag-on path. One reference line per boundary
 * BETWEEN adjacent bands (not one per band) — zone fills are M3, this is
 * boundary lines only. `bands` must already be ordered lowest→highest
 * (the shape every domain band-builder/query returns them in).
 *
 * Each boundary's colour/label comes from whichever of its two adjacent
 * bands has the higher `severity` — the zone you're entering as you move
 * away from "good" — matching `buildIndicatorReferenceLines`'s convention
 * of labelling a tolerance/appetite line by the worse side (e.g.
 * `upperTolerance` uses the "Outside" rating, not "Outside appetite", even
 * though it's the boundary between them). For a seeded config this is
 * moot either way: the low- and high-side bands of a given category
 * already share the same label/colour (see
 * `resolveSeededBandingLabels`/`buildSeededBandingConfig`), so picking
 * either adjacent band at a shared-category boundary renders identically.
 */
export const buildIndicatorReferenceLinesFromConfig = (
  bands: IndicatorBand[]
): IndicatorReferenceLine[] => {
  const lines: IndicatorReferenceLine[] = [];

  for (let i = 0; i < bands.length - 1; i++) {
    const lower = bands[i]!;
    const upper = bands[i + 1]!;
    if (!lower.upper) {
      continue;
    }

    const moreSevere = lower.severity > upper.severity ? lower : upper;
    lines.push({
      value: lower.upper.value,
      color: moreSevere.colour,
      label: moreSevere.label,
    });
  }

  return lines;
};
